import { Box, type SxProps, type Theme } from '@mui/material';
import { createContext, useContext, useRef } from 'react';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';

type SmartReportDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: string;
  useBackdrop?: boolean;
};

type BackdropProps = {
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
};

type ContentProps = {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
};

type DrawerContextType = {
  isOpen: boolean;
  onClose: () => void;
  width: string;
  backdropRef: React.RefObject<HTMLDivElement | null>;
  drawerRef: React.RefObject<HTMLDivElement | null>;
  shouldRender: boolean;
  useBackdrop: boolean;
};

const DrawerContext = createContext<DrawerContextType | null>(null);

const useDrawerContext = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('SmartReportDrawer components must be used within SmartReportDrawer.Root');
  }
  return context;
};

const Backdrop = ({ children, sx = {} }: BackdropProps) => {
  const { onClose, backdropRef, useBackdrop } = useDrawerContext();

  if (!useBackdrop) {
    return null;
  }

  return (
    <Box
      ref={backdropRef}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        ...sx,
      }}
      onClick={onClose}
    >
      {children}
    </Box>
  );
};

const Content = ({ children, sx = {} }: ContentProps) => {
  const { width, drawerRef, isOpen } = useDrawerContext();

  return (
    <Box
      component='aside'
      ref={drawerRef}
      sx={{
        position: 'relative',
        width: {
          xs: isOpen ? width : 0,
          lg: width,
        },
        flexShrink: 0,
        overflow: 'hidden',
        transition: 'width 0.3s cubic-bezier(0, 0.5, 0.5, 1)',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

const ESC = 'Escape';

const Root = ({
  isOpen,
  onClose,
  children,
  width = 'clamp(15rem, 20%, 30rem)',
  useBackdrop = false,
}: SmartReportDrawerProps) => {
  const backdropRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  useKeyboardShortcuts({
    [ESC]: () => {
      if (isOpen) {
        onClose();
      }
    },
  });

  const contextValue: DrawerContextType = {
    isOpen,
    onClose,
    width,
    backdropRef,
    drawerRef,
    shouldRender: true,
    useBackdrop,
  };

  return <DrawerContext.Provider value={contextValue}>{children}</DrawerContext.Provider>;
};

export const SmartReportDrawer = {
  Root,
  Backdrop,
  Content,
};
